import { useMemo, useRef, useEffect } from 'react';
import { Alert, Box, Button, Link, Text, Title } from '@nimbus-ds/components';
import { nanoid } from '@reduxjs/toolkit';
import { PaymentOptionInfoDto } from '@tiendanube/common';
import { Stack } from '@tiendanube/components';
import ModalAside from 'App/components/lab/ModalAside';
import { useNavegate } from 'App/hooks';
import {
  HeaderTop,
  IconSaveLink,
  VirtualInteractiveListSkeleton,
} from 'commons/components';
import { isNuvemPagoProvider } from 'commons/utils';
import { useTranslationMarketing } from 'domains/Marketing/hook';
import { useGetActivePaymentOptions } from 'domains/Payments/PaymentProviders/hooks';
import { PAYMENTS_BASE_ROUTE } from 'domains/Payments/paymentsRoutes';
import { PaymentOptionList } from './PaymentOptionList';
import { PaymentOptionListEmpty } from './PaymentOptionListEmpty';
import { PaymentOptionsDisabledAlert } from './PaymentOptionsDisabledAlert';
import {
  buildPaymentOptionInfo,
  filterNuvemPagoOptions,
  findMatchingPaymentProvider,
  hasAnyPaymentOptionDisabled,
} from './utils';

interface PaymentOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChange: (paymentOptions: PaymentOptionInfoDto[]) => void;
  paymentOptions: PaymentOptionInfoDto[];
  shouldLimitToNuvemPago: boolean;
}

export function PaymentOptionsModal({
  isOpen,
  onClose,
  onChange,
  paymentOptions,
  shouldLimitToNuvemPago,
}: Readonly<PaymentOptionsModalProps>): JSX.Element {
  const t = useTranslationMarketing(
    'promotions.settingsPage.limits.paymentOptions.modal',
  );

  const { isLoading, isError, isSuccess, activePaymentOptions, reload } =
    useGetActivePaymentOptions();

  const { goTo } = useNavegate();
  const handleGoToPayments = () => {
    onClose();
    goTo(PAYMENTS_BASE_ROUTE);
  };

  const shouldFilterNuvemPagoOptions =
    shouldLimitToNuvemPago && activePaymentOptions.length > 0;
  const hasAnyOptionSelected = paymentOptions.length > 0;

  const filteredActivePaymentOptions = useMemo(() => {
    if (shouldFilterNuvemPagoOptions) {
      return activePaymentOptions.filter((provider) =>
        isNuvemPagoProvider(provider.paymentProviderName),
      );
    }

    return activePaymentOptions;
  }, [shouldFilterNuvemPagoOptions, activePaymentOptions]);

  useEffect(() => {
    if (shouldFilterNuvemPagoOptions && hasAnyOptionSelected) {
      const filteredSelectedOptions = filterNuvemPagoOptions(
        paymentOptions,
        activePaymentOptions,
      );

      if (filteredSelectedOptions.length !== paymentOptions.length) {
        onChange(filteredSelectedOptions);
      }
    }
  }, [
    shouldFilterNuvemPagoOptions,
    hasAnyOptionSelected,
    paymentOptions,
    activePaymentOptions,
    onChange,
  ]);

  const shouldShowDisabledWarning = hasAnyPaymentOptionDisabled(
    filteredActivePaymentOptions,
  );

  const mappedActivePaymentOptions: PaymentOptionInfoDto[] = useMemo(
    () =>
      filteredActivePaymentOptions.flatMap((activePaymentOption) =>
        activePaymentOption.checkoutPaymentOptions.map((option) =>
          buildPaymentOptionInfo(activePaymentOption, option),
        ),
      ),
    [filteredActivePaymentOptions],
  );

  const selectableOptions = useMemo(
    () =>
      mappedActivePaymentOptions.filter((paymentOption) => {
        const found = filteredActivePaymentOptions
          .flatMap((paymentProvider) => paymentProvider.checkoutPaymentOptions)
          .find((option) => option.id === paymentOption.id);

        return found?.legacyDiscount === 0;
      }),
    [mappedActivePaymentOptions, filteredActivePaymentOptions],
  );

  const handleSelectAll = () => onChange(selectableOptions);

  const handleClearAll = () => onChange([]);

  const hasActivePaymentOptions = filteredActivePaymentOptions.length > 0;

  const addPaymentOption = (optionId: string) => {
    const matchingPaymentProvider = findMatchingPaymentProvider(
      filteredActivePaymentOptions,
      optionId,
    );
    const matchingActivePaymentOption =
      matchingPaymentProvider?.checkoutPaymentOptions.find(
        (option) => option.id === optionId,
      );

    if (
      matchingPaymentProvider &&
      matchingActivePaymentOption &&
      !paymentOptions.some((option) => option.id === optionId)
    ) {
      onChange([
        ...paymentOptions,
        buildPaymentOptionInfo(
          matchingPaymentProvider,
          matchingActivePaymentOption,
        ),
      ]);
    }
  };

  const removePaymentOption = (optionId: string) => {
    onChange(paymentOptions.filter((opt) => opt.id !== optionId));
  };

  const handlePaymentOptionToggle = ({ name, checked }) => {
    if (!hasActivePaymentOptions) {
      return;
    }
    checked ? addPaymentOption(name) : removePaymentOption(name);
  };

  const headerNavigation = { onClick: onClose };

  const isEmpty = isSuccess && !hasActivePaymentOptions;

  const emptyTranslationKey = shouldLimitToNuvemPago
    ? 'emptyNuvemPago'
    : 'emptyList';

  const formInstanceIdRef = useRef<string>();
  formInstanceIdRef.current ??= nanoid();
  const formInstanceId = formInstanceIdRef.current;

  return (
    <ModalAside
      hasVirtualizationContent
      isOpen={isOpen}
      onDidDismiss={onClose}
      headerTop={
        <HeaderTop
          navigation={headerNavigation}
          actions={<IconSaveLink onClick={onClose} />}
        />
      }
      headerContent={<Title fontSize="h2">{t('title')}</Title>}
    >
      {isLoading && <VirtualInteractiveListSkeleton />}

      {isError && (
        <Alert appearance="danger">
          <Box display="flex" flexDirection="column" gap="2">
            <Text fontWeight="bold">{t('error.text')}</Text>
            <Link onClick={reload}>{t('error.retry')}</Link>
          </Box>
        </Alert>
      )}

      {isEmpty ? (
        <PaymentOptionListEmpty
          handleGoToPayments={handleGoToPayments}
          translationKey={emptyTranslationKey}
        />
      ) : (
        <Stack column align="stretch">
          {selectableOptions.length > 0 && (
            <Stack.Item>
              <Text color="neutral-textHigh">{t('subtitle')}</Text>

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                marginTop="7"
              >
                {hasAnyOptionSelected ? (
                  <>
                    <Text color="neutral-textDisabled">
                      {paymentOptions.length === 1
                        ? t('selected', { count: paymentOptions.length })
                        : t('selectedPlural', { count: paymentOptions.length })}
                    </Text>

                    <Button appearance="neutral" onClick={handleClearAll}>
                      <Text color="neutral-textLow">{t('cleanAll')}</Text>
                    </Button>
                  </>
                ) : (
                  <Button appearance="neutral" onClick={handleSelectAll}>
                    <Text color="neutral-textLow">{t('selectAll')}</Text>
                  </Button>
                )}
              </Box>
            </Stack.Item>
          )}

          {shouldShowDisabledWarning && (
            <Stack.Item>
              <PaymentOptionsDisabledAlert
                handleGoToPayments={handleGoToPayments}
                keyName={`disabled-payment-warning-${formInstanceId}`}
              />
            </Stack.Item>
          )}

          {shouldFilterNuvemPagoOptions && (
            <Stack.Item>
              <Alert appearance="warning">
                <Text>{t('nuvemPagoLimitWarning')}</Text>
              </Alert>
            </Stack.Item>
          )}

          <Stack.Item>
            <PaymentOptionList
              paymentOptions={paymentOptions}
              activePaymentOptions={filteredActivePaymentOptions}
              onChange={handlePaymentOptionToggle}
            />
          </Stack.Item>
        </Stack>
      )}
    </ModalAside>
  );
}
